import { AttendanceStatus, Attendee, EventTicketTier } from "@prisma/client";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useState } from "react";
import PurchaseTicketTierCard from "~/app/community-management/[communitySlug]/ticket-packages/_components/PurchaseTicketTierCard";
import Spinner from "~/components/themed/spinner";
import { EventBySlugOutput } from "~/types/EventTypesInferred";
import { toTitleCase } from "~/utils/string";
import {
    STRIPE_CURRENCIES,
    StripeCurrency,
} from "~/utils/stripe_currency_codes";
import { Button } from "../themed/Button";
import GuestRSVPLoginModal from "../themed/GuestRSVPLoginModal";
import PurchaseMembershipOrOneOffByPass from "~/app/v1/[communitySlug]/memberships/_components/PurchaseMembershipOrOneOffByPass";
import { WalletContextState } from "@solana/wallet-adapter-react";

export enum EVENT_RSVP_STATUS {
    ASK_TO_JOIN,
    MEMBERS_ONLY_TAG_GATED,
    MEMBERS_ONLY,
    TOKEN_GATED,
    LOADING,
    LOGIN_TO_RSVP,
    HOSTING,
    JOIN_WAITLIST,
    ON_WAITLIST,
    INVITED,
    FULL,
    DISABLED,
    ATTENDING,
    OTHER_STATUS,
    RSVP_AVAILABLE,
    PENDING_APPROVAL,
    AWAITING_TICKET_TIER_SELECTION,
}

const isOnWaitlistOrAttending = (attendee: Attendee | null | undefined) => {
    return (
        attendee?.status === AttendanceStatus.WAITLIST ||
        attendee?.status === AttendanceStatus.ATTENDING
    );
};

export const determineEventStatus = (
    event: EventBySlugOutput,
    attendee: Attendee | null | undefined,
    isHost: boolean,
    spotsLeft: number,
    isLoading: boolean,
    sessionStatus: "authenticated" | "loading" | "unauthenticated",
    hasNFT: boolean,
): EVENT_RSVP_STATUS => {
    if (isHost) {
        return EVENT_RSVP_STATUS.HOSTING;
    }

    if (attendee?.status === AttendanceStatus.ATTENDING) {
        return EVENT_RSVP_STATUS.ATTENDING;
    }

    if (attendee?.status === AttendanceStatus.REJECTED) {
        return EVENT_RSVP_STATUS.ON_WAITLIST;
    }

    if (event.useTicketPackage) {
        if (!!event.eventTicketPackage) {
            return EVENT_RSVP_STATUS.AWAITING_TICKET_TIER_SELECTION;
        }
    }

    if (attendee?.status === AttendanceStatus.PENDING_APPROVAL) {
        return EVENT_RSVP_STATUS.PENDING_APPROVAL;
    }

    if (event?.isPreapproved && !isOnWaitlistOrAttending(attendee)) {
        return EVENT_RSVP_STATUS.ASK_TO_JOIN;
    }

    if (
        isEventFull(event) &&
        event?.community?.isWaitlistEnabled &&
        attendee?.status !== AttendanceStatus.WAITLIST
    ) {
        return EVENT_RSVP_STATUS.JOIN_WAITLIST;
    }

    // TODO: Tag gating check can go up here after we have guest checkout / purchase
    // finished
    if (
        event?.isMembersOnlyTagGated &&
        !event?.currentUserIsMemberOfEventCommunity
    ) {
        return EVENT_RSVP_STATUS.MEMBERS_ONLY_TAG_GATED;
    }

    if (event?.isMembersOnly && !event?.currentUserIsMemberOfEventCommunity) {
        return EVENT_RSVP_STATUS.MEMBERS_ONLY;
    }

    if (event?.nftTokenGateCollectionId && !hasNFT) {
        return EVENT_RSVP_STATUS.TOKEN_GATED;
    }

    if (sessionStatus === "loading" || isLoading) {
        return EVENT_RSVP_STATUS.LOADING;
    }

    if (sessionStatus === "unauthenticated") {
        return EVENT_RSVP_STATUS.LOGIN_TO_RSVP;
    }

    if (attendee?.status === AttendanceStatus.WAITLIST) {
        return EVENT_RSVP_STATUS.ON_WAITLIST;
    }

    if (isEventFull(event)) {
        if (attendee) {
            return EVENT_RSVP_STATUS.FULL;
        }
        return EVENT_RSVP_STATUS.DISABLED;
    }
    if (attendee && attendee.status === "INVITED") {
        return EVENT_RSVP_STATUS.INVITED;
    }

    if (
        attendee &&
        attendee.status !== "MAYBE" &&
        attendee.status !== "NOT_ATTENDING"
    ) {
        return EVENT_RSVP_STATUS.OTHER_STATUS;
    }

    return EVENT_RSVP_STATUS.RSVP_AVAILABLE;
};

export const isEventFull = (event: EventBySlugOutput) => {
    const attendingCount =
        event?.attendees?.filter((a) => a.status === "ATTENDING").length || 0;
    return event?.capacity && attendingCount >= event?.capacity;
};

// Render functions for different statuses
export const renderMembersOnlyTagGated = (event: EventBySlugOutput) => {
    return (
        <PurchaseMembershipOrOneOffByPass
            eventSlug={event.slug}
            tierId={event?.requiredMembershipTier?.id}
            communityId={event?.community?.id}
            isMember={event?.currentUserIsMemberOfEventCommunity}
        />
    );
};

export const renderMembersOnly = () => (
    <Button className="w-full" variant="info" disabled={true}>
        This event is for members only
    </Button>
);

export const renderTokenGated = () => (
    <Button className="w-full" variant="info" disabled={true}>
        This event is for token holders only
    </Button>
);

export const renderLoading = () => (
    <Button className="w-full" disabled={true}>
        <Spinner className="text-v3-N-500" type="small" />
    </Button>
);

export const renderHosting = () => (
    <Button className="w-full" variant="info" disabled={true}>
        You're hosting this event
    </Button>
);

export const renderOnWaitlist = () => (
    <Button className="w-full" variant="info" disabled={true}>
        <Image src="/icons/checkmark.svg" alt="check" width={22} height={22} />
        On Waitlist
    </Button>
);

export const renderFull = (event: EventBySlugOutput) => {
    return (
        <Button className="w-full" variant="info" disabled={true}>
            Event is full
        </Button>
    );
};

export const renderPending = () => {
    return (
        <Button className="w-full" variant="info" disabled={true}>
            Pending approval
        </Button>
    );
};

export const renderAttending = (attendee: Attendee | null | undefined) => (
    <Button className="w-full" variant="info" disabled={true}>
        <Image src="/icons/checkmark.svg" alt="check" width={22} height={22} />
        Attending
    </Button>
);

export const renderAwaitingTicketTierSelection = ({
    event,
    handleRegistration,
    setSelectedTier,
    setDiscountCode,
}: {
    event: EventBySlugOutput;
    handleRegistration: () => void;
    setSelectedTier: (selectedTier: EventTicketTier | null) => void;
    setDiscountCode: (discountCode: string) => void;
}) => {
    const handlePurchaseSelectedTier = () => {
        handleRegistration();
    };

    return (
        <>
            <PurchaseTicketTierCard
                event={event}
                setSelectedTier={setSelectedTier}
                setDiscountCode={setDiscountCode}
                onSelection={handlePurchaseSelectedTier}
            />
        </>
    );
};

export const renderOtherStatus = (attendee: Attendee | null | undefined) => {
    return (
        <Button className="w-full" variant="info">
            {toTitleCase(attendee?.status ?? "")}
        </Button>
    );
};

export const renderLoginPrompt = (event: EventBySlugOutput) => {
    const router = useRouter();
    const [showModal, setShowModal] = useState(false);
    const defaultCurrency = STRIPE_CURRENCIES[0];
    const initialCurrency =
        STRIPE_CURRENCIES.find((c) => c.code === event.currencyCode) ??
        defaultCurrency;
    const [currency, setCurrency] = useState<StripeCurrency>(
        initialCurrency as StripeCurrency,
    );
    return (
        <>
            <Button
                onClick={() => {
                    setShowModal(true);
                }}
                className="w-full"
            >
                Log in or Sign up to Attend{" "}
                {event.priceDollars ? (
                    <span className="flex items-center gap-1">
                        {currency.icon}
                        {`${event.priceDollars}.${event.priceCents ?? "00"}`}
                    </span>
                ) : (
                    ""
                )}
            </Button>
            <GuestRSVPLoginModal
                isOpen={showModal}
                setIsOpen={setShowModal}
                event={event}
            />
        </>
    );
};

export const renderAskToJoin = (handleJoinWaitlist: () => void) => {
    return (
        <Button className="w-full" onClick={handleJoinWaitlist}>
            Request to Join
        </Button>
    );
};

export const renderJoinWaitlist = (handleJoinWaitlist: () => void) => {
    return (
        <Button className="w-full" onClick={handleJoinWaitlist}>
            Join Waitlist
        </Button>
    );
};
